import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Edit, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Button from "../../common/components/Button";
import PageHeader from "../../common/components/PageHeader";
import Pagination from "../../common/components/Pagination";
import { useDeletePayout, usePayouts } from "../api/payouts";
import AddPayoutModal from "../components/AddPayoutModal";

const Payouts = () => {
  const { t } = useTranslation("payouts");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const { payouts, pagination, loading, refetch } = usePayouts(
    currentPage,
    pageSize,
  );
  const { deletePayout } = useDeletePayout();
  const [error, setError] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [payoutToDelete, setPayoutToDelete] = useState(null);
  const [editingPayout, setEditingPayout] = useState(null);

  const handleAddPayout = () => {
    setEditingPayout(null);
    setAddModalOpen(true);
  };

  const handleEditPayout = (payout) => {
    setEditingPayout(payout);
    setAddModalOpen(true);
  };

  const handleDeleteClick = (payout) => {
    setPayoutToDelete(payout);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setError(null);

      await deletePayout(payoutToDelete.id);
      await refetch(); // Refresh the list
      setDeleteDialogOpen(false);
      setPayoutToDelete(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleModalClose = () => {
    setAddModalOpen(false);
    setEditingPayout(null);
  };

  const handlePayoutCreated = async () => {
    await refetch(); // Refresh the list after create/update
    handleModalClose();
  };

  const formatAmount = (amount) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Calculate pagination from API response
  const totalItems = pagination.count;
  const totalPages = Math.ceil(totalItems / pageSize);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <Spinner animation="border" />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={t("title")}
        description={t("description")}
        appendActions={
          <Button
            variant="contained"
            startIcon={<Plus size={20} />}
            onClick={handleAddPayout}
          >
            {t("addPayout")}
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t("table.paidBy")}</TableCell>
              <TableCell>{t("table.paidTo")}</TableCell>
              <TableCell>{t("table.amount")}</TableCell>
              <TableCell>{t("table.asset")}</TableCell>
              <TableCell>{t("table.notes")}</TableCell>
              <TableCell>{t("table.created")}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payouts?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="bodySm" color="text.secondary">
                    {t("table.noPayouts")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              payouts.map((payout) => (
                <TableRow key={payout.id} hover>
                  <TableCell>
                    <Typography variant="bodySm" fontWeight="medium">
                      {payout.paid_by_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="bodySm" fontWeight="medium">
                      {payout.paid_to_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="bodySm">
                      {formatAmount(payout.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {payout.line_items?.length > 0 ? (
                      <Box>
                        {payout.line_items.map((li) => (
                          <Typography key={li.id} variant="bodySm">
                            {li.asset_title}: $
                            {parseFloat(li.amount).toFixed(2)}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="bodySm">
                        {payout.asset_title || "-"}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="bodySm"
                      color="text.secondary"
                      sx={{
                        maxWidth: "200px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {payout.notes || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="bodySm" color="text.secondary">
                      {formatDate(payout.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEditPayout(payout)}
                      sx={{ mr: 1 }}
                    >
                      <Edit size={16} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(payout)}
                      color="error"
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setCurrentPage(1);
          }}
        />
      )}

      <AddPayoutModal
        open={addModalOpen}
        onClose={handleModalClose}
        onPayoutCreated={handlePayoutCreated}
        editingPayout={editingPayout}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("deleteDialog.title")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("deleteDialog.message", {
              amount: payoutToDelete && formatAmount(payoutToDelete.amount),
              paidBy: payoutToDelete?.paid_by_name,
              paidTo: payoutToDelete?.paid_to_name,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={() => setDeleteDialogOpen(false)}
          >
            {t("deleteDialog.cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            {t("deleteDialog.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payouts;
